import {inject, Injectable, OnDestroy} from '@angular/core';
import {MediaHelperService} from '../core/media-helper.service';
import {SuperObjectModel} from '../core/definitions/super-object-model';
import {isMediaNotReady, MAX_CONVERSION_POLL_MS} from './thumbnail-fallback-icon';

const STATUS_POLL_INTERVAL_MS = 5000;

// Follows the DMS conversion of media items, so a tile shows the hourglass while its media
// converts and its thumbnail once it is done, without being selected. Items are watched when their
// thumbnail fails, and audio items when the carousel loads, as their placeholder image never fails.
// The statuses of all items are fetched together, and the converting ones polled together.
// Shared by the media carousel and the object content list, which each provide their own instance,
// so polling stops when the carousel or list is destroyed. Items are tracked by media id, which
// defaults to the artifact id; several items can share one media id.
@Injectable()
export class MediaConversionWatcher implements OnDestroy {
  private readonly mediaHelper: MediaHelperService = inject(MediaHelperService);
  // All items watched per media id (also the objects the owner rebuilds when the object is reloaded)
  private readonly itemsById = new Map<string, Set<SuperObjectModel>>();
  // The last known status per media id, '' when it could not be found. Each media id is looked up once
  // per watcher: a thumbnail that still fails after its conversion is done is really missing.
  private readonly statusById = new Map<string, string>();
  private readonly pendingIds = new Set<string>();
  private readonly convertingIds = new Set<string>();
  // When each converting id was first seen not ready: it is polled for at most MAX_CONVERSION_POLL_MS
  private readonly startedAtById = new Map<string, number>();
  private timer: ReturnType<typeof setTimeout> = null;
  private checking = false;
  private destroyed = false;

  watch(media: SuperObjectModel, mediaId: string = media?.artifact_id) {
    if (media && mediaId) {
      this.register(media, mediaId);
      this.scheduleCheckForPending();
    }
  }

  watchAll(mediaList: SuperObjectModel[]) {
    mediaList.filter(media => media?.artifact_id).forEach(media => this.register(media, media.artifact_id));
    this.scheduleCheckForPending();
  }

  // True until the first lookup of a watched media id has finished, also when it found no status
  isAwaitingStatus(mediaId: string): boolean {
    return this.itemsById.has(mediaId) && !this.statusById.has(mediaId);
  }

  ngOnDestroy() {
    this.destroyed = true;
    clearTimeout(this.timer);
    this.timer = null;
  }

  private register(media: SuperObjectModel, mediaId: string) {
    if (!this.itemsById.has(mediaId)) {
      this.itemsById.set(mediaId, new Set());
    }
    this.itemsById.get(mediaId).add(media);
    if (!this.statusById.has(mediaId)) {
      this.pendingIds.add(mediaId);
    } else if (this.statusById.get(mediaId)) {
      media.$$dmsStatus = this.statusById.get(mediaId);
    }
  }

  private scheduleCheckForPending() {
    if (this.pendingIds.size) {
      // On the next tick, so the thumbnails that fail together are looked up in one request
      this.scheduleCheck(0);
    }
  }

  private scheduleCheck(delayMs: number) {
    if (this.destroyed || this.checking || (this.timer && delayMs > 0)) {
      return;
    }
    clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.timer = null;
      this.check().then();
    }, delayMs);
  }

  private async check() {
    this.dropExpired();
    const ids = [...new Set([...this.pendingIds, ...this.convertingIds])];
    this.pendingIds.clear();
    if (!ids.length) {
      return;
    }
    this.checking = true;
    const statuses = await this.mediaHelper.getMediaUploadStatuses(ids);
    this.checking = false;
    if (this.destroyed) {
      return;
    }
    // A failed lookup (null) must not end the conversion polling: converting items keep their status
    ids.filter(mediaId => statuses || !this.convertingIds.has(mediaId))
      .forEach(mediaId => this.setStatus(mediaId, statuses?.[mediaId] ?? ''));
    this.scheduleNext();
  }

  private dropExpired() {
    const now = Date.now();
    [...this.convertingIds]
      .filter(mediaId => now - this.startedAtById.get(mediaId) >= MAX_CONVERSION_POLL_MS)
      .forEach(mediaId => this.stopPolling(mediaId));
  }

  private stopPolling(mediaId: string) {
    this.convertingIds.delete(mediaId);
    this.startedAtById.delete(mediaId);
  }

  private scheduleNext() {
    if (this.pendingIds.size) {
      this.scheduleCheck(0);
    } else if (this.convertingIds.size) {
      this.scheduleCheck(STATUS_POLL_INTERVAL_MS);
    }
  }

  private setStatus(mediaId: string, status: string) {
    const previous = this.statusById.get(mediaId);
    const items = [...this.itemsById.get(mediaId)];
    this.statusById.set(mediaId, status);
    if (status) {
      items.forEach(item => item.$$dmsStatus = status);
    }
    if (isMediaNotReady(status)) {
      this.convertingIds.add(mediaId);
      if (!this.startedAtById.has(mediaId)) {
        this.startedAtById.set(mediaId, Date.now());
      }
      return;
    }
    this.stopPolling(mediaId);
    if (status === 'done' && isMediaNotReady(previous)) {
      // A new url makes the tile try the thumbnail again
      items.forEach(item => {
        this.refreshUrl(item, '$$thumbUrl');
        this.refreshUrl(item, '$$thumbnailUrl');
      });
    }
  }

  private refreshUrl(media: SuperObjectModel, field: '$$thumbUrl' | '$$thumbnailUrl') {
    if (media[field]) {
      media[field] = this.mediaHelper.getImageUrlWithRandom(media[field]);
    }
  }
}
